import React from 'react'
import { Element } from 'react-scroll'
import styled from 'styled-components'
import { DESKTOP } from '@streamr/streamr-layout/styled'
import graph from './graph.svg'
import graphVertical from './graph_vertical.svg'
import Hero from './Hero'
import WhyBuild from './WhyBuild'
import MetricTile from './MetricTile'
import CoreGallery from './CoreGallery'
import SwashGallery from './SwashGallery'
import HowDataUnionsWork from './HowDataUnionsWork'
import useData from './useData'
import Graph from '~shared/Graph'
import Topic from '~shared/Topic'
import Container from '~shared/Container'
import useTranslation from '~utils/useTranslation'
import Display from '~shared/Display'
import Layout from '~shared/Layout'
import Figure from '~shared/Figure'
import Spacer from '~shared/Spacer'
import ScrollEncourager from '~shared/ScrollEncourager'

const StyledScrollEncourager = styled(ScrollEncourager)`
    margin: 7rem auto 0;

    @media (max-width: ${DESKTOP}px) {
        display: none;
    }
`

const CaseStudiesTopic = styled(Topic)`
    @media (max-width: ${DESKTOP}px) {
        > ${Container} {
            max-width: 32rem;

            ${Topic.Title} {
                text-align: left;
                margin-left: 0;
            }
        }
    }
`

const UnstyledDataUnions = (props) => {
    const { t } = useTranslation()

    const {
        historicalMembers,
        dates,
        historicalEarnings,
        historicalPurchaseCounts,
        historicalCounts,
    } = useData()

    return (
        <Layout {...props}>
            <Hero />
            <StyledScrollEncourager offset={60} onBright />
            <Element name={ScrollEncourager.TargetID} />
            <Spacer head="5rem,6.5rem,7.5rem" tail="0,3rem,14.5rem">
                <WhyBuild />
            </Spacer>
            <Display mobile="none" tablet="block" $appear>
                <Topic pad head="13rem,,17.5rem" tail="10.5rem,,15rem" backgroundColor="#121622" color="#ffffff">
                    <Topic.Body>
                        <Figure>
                            <CoreGallery />
                            <Spacer as={Figure.Caption} head=",48px,40px" $appear>
                                Streamr Core views for creating and managing a Data Union
                            </Spacer>
                        </Figure>
                    </Topic.Body>
                </Topic>
            </Display>
            <Topic head="6rem,12.5rem,13rem" tail="5.5rem,10.5rem,25rem" color="#0C009A">
                <Topic.Title hash="ecosystem" tail="4.5rem,9rem,12.5rem" $appear>
                    <Display as="span" mobile="none" tablet="inline">
                        {t('learn.dataUnions.ecoSystem.title')}
                    </Display>
                    <Display as="span" tablet="none">
                        {t('learn.dataUnions.ecoSystem.short.title')}
                    </Display>
                </Topic.Title>
                <Topic.Body>
                    <Container>
                        <MetricTile.List>
                            <MetricTile $appear>
                                <MetricTile.Header
                                    title={(
                                        <>
                                            <Display as="h4" mobile="none" tablet="block">
                                                {t('learn.dataUnions.ecoSystem.metric.members')}
                                            </Display>
                                            <Display as="h4" tablet="none">
                                                {t('learn.dataUnions.ecoSystem.metric.short.members')}
                                            </Display>
                                        </>
                                    )}
                                    value={historicalMembers}
                                    loading={!(dates.length && historicalMembers.length)}
                                />
                                <MetricTile.Chart data={historicalMembers} dates={dates} />
                            </MetricTile>
                            <MetricTile $appear>
                                <MetricTile.Header
                                    title={(
                                        <>
                                            <Display as="h4" mobile="none" tablet="block">
                                                {t('learn.dataUnions.ecoSystem.metric.revenue')}
                                            </Display>
                                            <Display as="h4" tablet="none">
                                                {t('learn.dataUnions.ecoSystem.metric.short.revenue')}
                                            </Display>
                                        </>
                                    )}
                                    value={historicalEarnings}
                                    loading={!(dates.length && historicalEarnings.length)}
                                />
                                <MetricTile.Chart data={historicalEarnings} dates={dates} />
                            </MetricTile>
                            <MetricTile $appear>
                                <MetricTile.Header
                                    title={(
                                        <>
                                            <Display as="h4" mobile="none" tablet="block">
                                                {t('learn.dataUnions.ecoSystem.metric.purchases')}
                                            </Display>
                                            <Display as="h4" tablet="none">
                                                {t('learn.dataUnions.ecoSystem.metric.short.purchases')}
                                            </Display>
                                        </>
                                    )}
                                    value={historicalPurchaseCounts}
                                    loading={!(dates.length && historicalPurchaseCounts.length)}
                                />
                                <MetricTile.Chart data={historicalPurchaseCounts} dates={dates} />
                            </MetricTile>
                            <MetricTile $appear>
                                <MetricTile.Header
                                    title={(
                                        <>
                                            <Display as="h4" mobile="none" tablet="block">
                                                {t('learn.dataUnions.ecoSystem.metric.dataUnions')}
                                            </Display>
                                            <Display as="h4" tablet="none">
                                                {t('learn.dataUnions.ecoSystem.metric.short.dataUnions')}
                                            </Display>
                                        </>
                                    )}
                                    value={historicalCounts}
                                    loading={!(dates.length && historicalCounts.length)}
                                />
                                <MetricTile.Chart data={historicalCounts} dates={dates} />
                            </MetricTile>
                        </MetricTile.List>
                    </Container>
                </Topic.Body>
            </Topic>
            <CaseStudiesTopic pad head="5rem,11.5rem,16.5rem" tail="4rem,9.5rem,12.5rem" backgroundColor="#FAFAFA">
                <Container>
                    <Topic.Title hash="case-studies" $appear>
                        {t('learn.dataUnions.caseStudies.title')}
                    </Topic.Title>
                </Container>
                <Topic.Body $appear>
                    <SwashGallery />
                </Topic.Body>
            </CaseStudiesTopic>
            <Spacer head="1rem,2rem,8.75rem" tail="1rem,2rem,10rem">
                <HowDataUnionsWork />
            </Spacer>
            <Topic pad head="105px,170px,228px" tail="112px,125px,212px" backgroundColor="#14065A" color="#FFFFFF">
                <Topic.Body>
                    <Container>
                        <Display as={Figure} mobile="none" tablet="block">
                            <Graph.Image src={graph} alt="" $appear />
                            <Spacer as={Figure.Caption} head=",56px,128px" $appear>
                                {t('learn.dataUnions.graph.desktop_caption')}
                            </Spacer>
                        </Display>
                        <Display as={Figure} tablet="none">
                            <Graph.Image src={graphVertical} alt="" $appear />
                        </Display>
                    </Container>
                </Topic.Body>
            </Topic>
        </Layout>
    )
}

const DataUnions = styled(UnstyledDataUnions)`
    color: #0C009A;
`

export default DataUnions
